import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import * as _ from 'lodash';
import { OfferOption } from 'ng2-app/models/dto/offer-option/offer-option.model';
import {
  DeleteOfferOption,
  GetOfferOptions,
  CreateOfferOption,
  UpdateOfferOptionTerms,
  PatchOfferOptionStatus,
  AttachValuePropsToOption,
  AttachDeclineReasonsToOption,
  RecommendOfferOption,
  ResetRecommendedFlagOfferOptions,
} from './offer-options.actions';
import { OffersService } from 'app/services/offers/offers.service';
import { Injectable } from '@angular/core';
import { of, Subject, throwError } from 'rxjs';
import {catchError, take, tap} from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { patch, updateItem } from '@ngxs/store/operators';
import { ResetRecommendedFlagApprovals } from 'ng2-app/store/offers/approvals.actions';
import { ResetRecommendedFlagOffers } from 'ng2-app/store/offers/offer.actions';
import { DeclineReason } from 'app/models/dto/decline-reason.model';

export class OfferOptionsStateModel {
  offerOptions: OfferOption[];
  loading: boolean;
}

@State<OfferOptionsStateModel>({
	name: 'OfferOptions',
	defaults: {
    offerOptions: [],
    loading: false,
	}
})
@Injectable()

export class OfferOptionsState {

  @Selector()
	static offerOptions(state: OfferOptionsStateModel) {
    return state.offerOptions;
  }

  @Selector()
  static loading(state: OfferOptionsStateModel) {
    return state.loading;
  }

  @Selector()
  static dealOptions(state: OfferOptionsStateModel) {
    return (dealId: number) => state.offerOptions.filter(o => o.dealId === dealId);
  }

  destroyed$ = new Subject<boolean>();

  constructor(
    private offersService: OffersService,
    private store: Store,
    private snackBar: MatSnackBar,
  ) {}

	@Action(GetOfferOptions)
	getOfferOptions({ patchState }: StateContext<OfferOptionsStateModel>, { borrowerId }: GetOfferOptions) {
    patchState({
      offerOptions: [],
      loading: true,
    });
    this.offersService.getOfferOptionsByBorrowerId(borrowerId)
      .pipe(
        take(1),
        catchError( error => {
          console.error(error);
          return of();
        }),
      ).subscribe((unsortedOfferOptions: OfferOption[]) => {
        const offerOptions = unsortedOfferOptions?.sort(this.offerOptionsSort);
        patchState({
          offerOptions,
          loading: false,
        });
      });
  }

  @Action(DeleteOfferOption)
  deleteOfferOption({ patchState, getState }: StateContext<OfferOptionsStateModel>, { dealId, offerOptionId }: DeleteOfferOption) {
    return this.offersService.deleteOfferOption(dealId, offerOptionId)
      .pipe(
        catchError( error => {
          console.error(error);
          return of();
        }),
        tap(() => {
          const newState = getState().offerOptions.filter(offerOption => offerOption.id !== offerOptionId);
          patchState({ offerOptions: [...newState] });
        }),
      );
  }

  offerOptionsSort(a, b) {
    const idA = a.id;
    const idB = b.id;

    if (idA > idB) { return 1;  }
    if (idA < idB) { return -1; }
    return 1;
  }

  @Action(CreateOfferOption)
  createOfferOption({ getState, patchState }: StateContext<OfferOptionsStateModel>, {dealId, offerOptionData }: CreateOfferOption) {
    this.offersService.createOfferOption(dealId, offerOptionData).pipe(
      catchError( error => {
        this.snackBar.open('Failed to create an offer option', '', {
          verticalPosition: 'bottom',
          horizontalPosition: 'center',
          panelClass: 'lendio-alert--error',
          duration: 3000
        });
        console.error('[offer-options.state.ts] createOfferOption error: ', error);
        return throwError(error);
      }))
      .subscribe((newOfferOption: OfferOption) => {
        this.snackBar.open('Offer option created.', '', {
          verticalPosition: 'bottom',
          horizontalPosition: 'center',
          panelClass: 'lendio-alert--success',
          duration: 3000
        });

        patchState({ offerOptions: [...getState().offerOptions, newOfferOption] });
      });
  }

  @Action(UpdateOfferOptionTerms)
	updateOfferOptionTerms({ }: StateContext<OfferOptionsStateModel>, { dealId, offerOptionId, termSetData }: UpdateOfferOptionTerms) {
    this.offersService.updateOfferOptionTerms(dealId, offerOptionId, termSetData).pipe(catchError( error => {
          console.error(error);
          return of();
        })
      ).subscribe((success: boolean) => {});
  }

  @Action(PatchOfferOptionStatus)
  PatchOfferOptionStatus({ getState, patchState }: StateContext<OfferOptionsStateModel>,
    { dealId, offerOptionId, status, waitOnResponse }: PatchOfferOptionStatus) {

    const observer = this.offersService.patchOfferOptionStatus(dealId, offerOptionId, status).pipe(catchError( error => {
          console.error(error);
          return of();
        }),
        tap((updatedOfferOption: OfferOption) => {
          const offerOptionsStateCopy = [...getState().offerOptions];
          const offerOptionIndex = offerOptionsStateCopy.findIndex(o => o.id === offerOptionId);
          offerOptionsStateCopy[offerOptionIndex] = { ...offerOptionsStateCopy[offerOptionIndex], ...updatedOfferOption };
          patchState({ offerOptions: offerOptionsStateCopy });
        })
      );

    if (!waitOnResponse) {
      observer.subscribe();
      return;
    }

    return observer;
  }

  @Action(AttachValuePropsToOption)
  attachValuePropsToOption(
    { setState }: StateContext<OfferOptionsStateModel>,
    { offerOptionId, valuePropositionIds }: { offerOptionId: number, valuePropositionIds: number[] }
  ) {
    return this.offersService.attachValuePropositions('offerOption', offerOptionId, valuePropositionIds).pipe(
      tap((updatedOption) => {
        setState(
          patch<OfferOptionsStateModel>({
            offerOptions: updateItem(
              (offerOption) => offerOption && offerOption.id === offerOptionId,
              patch<OfferOption>({
                valuePropositions: updatedOption.valuePropositions,
              })
            ),
          })
        );
      })
    );
  }

  @Action(AttachDeclineReasonsToOption)
  attachDeclineReasons(
    { setState }: StateContext<OfferOptionsStateModel>,
    { offerOptionId, declineReasons }: { offerOptionId: number, declineReasons: Pick<DeclineReason, 'id' | 'comment'>[] }
  ) {
    return this.offersService.attachDeclineReasons('offerOption', offerOptionId, declineReasons).pipe(
      tap((updatedOption : OfferOption) => {
        setState(
          patch<OfferOptionsStateModel>({
            offerOptions: updateItem(
              (offerOption) => offerOption && offerOption.id === offerOptionId,
              patch<OfferOption>(updatedOption as OfferOption)
            ),
          })
        );
      })
    );
  }

  @Action(RecommendOfferOption)
  recommendOfferOption(
    { setState }: StateContext<OfferOptionsStateModel>,
    { offerOptionId, recommend }: { offerOptionId: number, recommend: boolean }
  ) {
    return this.offersService.recommendOption('offerOption', offerOptionId, recommend).pipe(
      tap((response) => {
        setState(
          patch<OfferOptionsStateModel>({
            offerOptions: updateItem(
              (offerOption) => offerOption && offerOption.id === offerOptionId,
              patch<OfferOption>({
                isRecommended: recommend,
              })
            ),
          })
        );
        const resets = response?.flagResets;
        if (!resets) {
          return;
        }
        if (resets.offerOption && resets.offerOption.length) {
          this.store.dispatch(new ResetRecommendedFlagOfferOptions(resets.offerOption));
        }
        if (resets.approval && resets.approval.length) {
          this.store.dispatch(new ResetRecommendedFlagApprovals(resets.approval));
        }
        if (resets.offer && resets.offer.length) {
          this.store.dispatch(new ResetRecommendedFlagOffers(resets.offer));
        }
      })
    );
  }

  @Action(ResetRecommendedFlagOfferOptions)
  resetRecommendedFlag(
    { setState }: StateContext<OfferOptionsStateModel>,
    { offerOptionIds }: { offerOptionIds: number[] }
  ) {
    setState(
      patch<OfferOptionsStateModel>({
        offerOptions: updateItem(
          (offerOption) => offerOption && offerOptionIds.includes(offerOption.id),
          patch<OfferOption>({
            isRecommended: false,
          })
        ),
      })
    );
  }
}
